import { addBreadcrumb, captureError } from './sentry';
import { toast } from '@/stores/toast-store';

const API_BASE = '/api/v1';
const AUTH_BASE = '/auth-api/v1';
const SP_BASE = '/sp-api/v1';
const RP_BASE = '/rp-api/v1';
const PRODUCTS_BASE = '/products-api/v1';
const SYNC_BASE = '/sync-api/v1';
// pos-backend (NestJS) — customer engagement endpoints live here at
// /api/v1/customer-engagement/*. The vite proxy strips /pos-api and the
// nginx config on findev routes /pos-api/* → pos-backend port 3000.
const POS_BACKEND_BASE = '/pos-api/api/v1';
const ESL_BASE = '/esl-api/v1';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

interface ApiError {
  error: {
    code: string;
    message: string;
    requestId?: string;
  };
}

// Deduplication for concurrent refresh calls
let refreshPromise: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${AUTH_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function extractRequestId(response: Response): string | undefined {
  return response.headers.get('X-Request-Id') || undefined;
}

/**
 * pos-backend (NestJS) answers an error as { statusCode, message, error:
 * 'Bad Request' } rather than our { error: { code, message } }. Its message
 * (a string, or a list of them) is meant for the user, so it is kept instead
 * of the bare status text.
 */
function nestErrorMessage(body: unknown): string | undefined {
  const message = (body as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message) return message;
  if (Array.isArray(message) && message.every((m) => typeof m === 'string')) return message.join('; ') || undefined;
  return undefined;
}

export type UploadProgress = (loadedBytes: number, totalBytes: number) => void;

/**
 * A multipart POST that reports upload progress.
 *
 * `fetch` cannot observe bytes going *out*, only coming back, so a 200 MB APK
 * upload through [request] looks frozen for minutes. XMLHttpRequest exposes
 * `upload.onprogress`; everything else — cookies, the one-shot token refresh
 * on 401, the error shape callers already handle — matches [request].
 */
function uploadWithProgress<T>(
  baseUrl: string,
  path: string,
  formData: FormData,
  onProgress?: UploadProgress,
  retried = false,
): Promise<T> {
  const url = `${baseUrl}${path}`;
  addBreadcrumb(`API POST ${path}`, { url, method: 'POST', upload: true });

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };
    }

    const parseError = (): ApiError => {
      const requestId = xhr.getResponseHeader('X-Request-Id') || undefined;
      let error: ApiError | null = null;
      try {
        error = JSON.parse(xhr.responseText) as ApiError;
      } catch {
        error = null;
      }
      if (!error || typeof error !== 'object' || typeof error.error !== 'object' || error.error === null) {
        error = { error: { code: 'UNKNOWN', message: xhr.statusText || 'Request failed', requestId } };
      } else if (requestId) {
        error.error.requestId = requestId;
      }
      return error;
    };

    xhr.onerror = () => {
      toast.error('Network unavailable');
      reject({ error: { code: 'NETWORK_ERROR', message: 'Network unavailable' } });
    };

    xhr.onload = async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject({ error: { code: 'UNKNOWN', message: 'Malformed response' } });
        }
        return;
      }

      // Same session handling as request(): an expired access token gets one
      // refresh and one retry, then the user is sent to sign in again.
      if (xhr.status === 401 && !retried) {
        const refreshed = await refreshTokens();
        if (refreshed) {
          uploadWithProgress<T>(baseUrl, path, formData, onProgress, true).then(resolve, reject);
          return;
        }
        const { useAuthStore } = await import('@/stores/auth-store');
        const { useLocationStore } = await import('@/stores/location-store');
        useAuthStore.getState().clearAuth();
        useLocationStore.getState().clearSelectedLocation();
        window.location.hash = '#/login';
        reject({ error: { code: 'UNAUTHORIZED', message: 'Session expired' } });
        return;
      }

      const error = parseError();
      if (xhr.status === 502 || xhr.status === 504) {
        // nginx answering for an API that went away mid-upload. The body is
        // an HTML page, not our JSON, so give the uploader something to act on.
        error.error.message =
          'The server stopped responding while receiving the file. Try again; if it keeps happening, the API may be short of memory or disk.';
      }
      reject(error);
    };

    xhr.send(formData);
  });
}

async function request<T>(baseUrl: string, path: string, options: RequestOptions = {}): Promise<T> {
  const { body, params, ...init } = options;

  let url = `${baseUrl}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  // FormData (file uploads) must be sent as-is so the browser can set the
  // multipart Content-Type with its boundary; JSON-encoding it or forcing
  // application/json would corrupt the upload.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const serializedBody = body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body);

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  // Only set Content-Type for JSON requests that have a body to avoid
  // Fastify rejecting empty JSON body with Content-Type: application/json.
  // For FormData, leave it unset so the browser adds the multipart boundary.
  if (body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  // Add Sentry breadcrumb for every API call
  addBreadcrumb(`API ${init.method || 'GET'} ${path}`, {
    url,
    method: init.method || 'GET',
  });

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers,
      credentials: 'include',
      body: serializedBody,
    });
  } catch (e) {
    // React Query cancels in-flight requests when a query unmounts or its key
    // changes. Surfacing those as "Network unavailable" toasts lies to the user.
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw e;
    }
    toast.error('Network unavailable');
    throw { error: { code: 'NETWORK_ERROR', message: 'Network unavailable' } };
  }

  // Handle 401 (expired token) and 403 NO_TENANT (stale JWT missing tenantId)
  // by refreshing the token and retrying. The JWT cookie may have an empty tenantId
  // from signup while the database already has the correct value after tenant assignment.
  const shouldRefreshAndRetry = (response.status === 401 || response.status === 403) && !path.startsWith('/auth/');

  if (shouldRefreshAndRetry) {
    // For 403, only retry if it's a NO_TENANT error (stale JWT)
    if (response.status === 403) {
      const body403: ApiError = await response
        .clone()
        .json()
        .catch(() => ({ error: { code: '' } }));
      if (body403.error?.code !== 'NO_TENANT') {
        // Not a stale-JWT issue — fall through to normal error handling
        const requestId = extractRequestId(response);
        let error = body403;
        if (error.error && typeof error.error === 'object') {
          if (requestId) error.error.requestId = requestId;
        } else {
          error = { error: { code: 'FORBIDDEN', message: nestErrorMessage(error) || 'Forbidden', requestId } };
        }
        throw error;
      }
    }

    const refreshed = await refreshTokens();
    if (refreshed) {
      // Retry the original request
      let retryResponse: Response;
      try {
        retryResponse = await fetch(url, {
          ...init,
          headers,
          credentials: 'include',
          body: serializedBody,
        });
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          throw e;
        }
        toast.error('Network unavailable');
        throw { error: { code: 'NETWORK_ERROR', message: 'Network unavailable' } };
      }
      if (!retryResponse.ok) {
        const requestId = extractRequestId(retryResponse);
        let error: ApiError = await retryResponse.json().catch(() => ({
          error: { code: 'UNKNOWN', message: retryResponse.statusText, requestId },
        }));
        if (!error || typeof error !== 'object' || typeof error.error !== 'object' || error.error === null) {
          error = {
            error: {
              code: 'UNKNOWN',
              message: nestErrorMessage(error) || retryResponse.statusText || 'Request failed',
              requestId,
            },
          };
        } else if (requestId) {
          error.error.requestId = requestId;
        }
        throw error;
      }
      return readBody(retryResponse);
    }
    // Refresh failed — clear all session state and redirect to login
    const { useAuthStore } = await import('@/stores/auth-store');
    const { useLocationStore } = await import('@/stores/location-store');
    useAuthStore.getState().clearAuth();
    useLocationStore.getState().clearSelectedLocation();
    window.location.hash = '#/login';
    throw { error: { code: 'UNAUTHORIZED', message: 'Session expired' } };
  }

  if (!response.ok) {
    const requestId = extractRequestId(response);
    let error: ApiError = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: response.statusText, requestId },
    }));
    if (!error || typeof error !== 'object' || typeof error.error !== 'object' || error.error === null) {
      error = {
        error: {
          code: 'UNKNOWN',
          message: nestErrorMessage(error) || response.statusText || 'Request failed',
          requestId,
        },
      };
    } else if (requestId) {
      error.error.requestId = requestId;
    }

    // Capture 5xx errors in Sentry (toast is handled by the global mutation onError)
    if (response.status >= 500) {
      captureError(new Error(`API ${response.status}: ${path}`), {
        requestId: requestId || 'unknown',
        statusCode: String(response.status),
        path,
        method: init.method || 'GET',
      });
    }

    throw error;
  }

  return readBody(response);
}

/** A success with no body (204, or an empty 200) resolves to undefined instead of failing to parse. */
async function readBody(response: Response) {
  if (response.status === 204) return undefined;
  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}

// Binary (file download) variant of request(): same cookie auth and
// refresh-and-retry on 401, but resolves to a Blob instead of parsed JSON.
// Errors still arrive as JSON bodies from the API, so failures parse the
// same ApiError shape request() throws.
async function requestBlob(baseUrl: string, path: string, body: unknown): Promise<Blob> {
  const url = `${baseUrl}${path}`;
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  };

  addBreadcrumb(`API POST ${path} (blob)`, { url, method: 'POST' });

  let response = await fetch(url, init);
  if (response.status === 401) {
    const refreshed = await refreshTokens();
    if (!refreshed) {
      throw { error: { code: 'UNAUTHORIZED', message: 'Session expired' } };
    }
    response = await fetch(url, init);
  }
  if (!response.ok) {
    const requestId = extractRequestId(response);
    const error: ApiError = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: response.statusText || 'Request failed', requestId },
    }));
    if (requestId && error.error && typeof error.error === 'object') {
      error.error.requestId = requestId;
    }
    throw error;
  }
  return response.blob();
}

function extractFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return match?.[1] || fallback;
}

/** Fetches a binary (PDF) response and triggers a browser download — for
 *  endpoints that return a generated file rather than JSON, e.g. statements. */
async function downloadPdf(
  baseUrl: string,
  path: string,
  params: Record<string, string | number | boolean | undefined> | undefined,
  fallbackFilename: string,
): Promise<void> {
  let url = `${baseUrl}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') searchParams.set(key, String(value));
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  let response: Response;
  try {
    response = await fetch(url, { credentials: 'include' });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    toast.error('Network unavailable');
    throw { error: { code: 'NETWORK_ERROR', message: 'Network unavailable' } };
  }

  // Mirror request<T>()'s silent refresh-and-retry: the access_token cookie
  // is short-lived and may have expired since page load, while refresh_token
  // is still valid. Without this, any statement download 401s the moment the
  // access token expires even though the user's session is still good.
  if (response.status === 401) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      try {
        response = await fetch(url, { credentials: 'include' });
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') throw e;
        toast.error('Network unavailable');
        throw { error: { code: 'NETWORK_ERROR', message: 'Network unavailable' } };
      }
    } else {
      const { useAuthStore } = await import('@/stores/auth-store');
      const { useLocationStore } = await import('@/stores/location-store');
      useAuthStore.getState().clearAuth();
      useLocationStore.getState().clearSelectedLocation();
      window.location.hash = '#/login';
      throw { error: { code: 'UNAUTHORIZED', message: 'Session expired' } };
    }
  }

  const contentType = response.headers.get('Content-Type') || '';
  if (!response.ok || !contentType.startsWith('application/pdf')) {
    const requestId = extractRequestId(response);
    let error: ApiError = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: response.statusText, requestId },
    }));
    if (!error || typeof error !== 'object' || typeof error.error !== 'object' || error.error === null) {
      error = { error: { code: 'UNKNOWN', message: response.statusText || 'Request failed', requestId } };
    } else if (requestId) {
      error.error.requestId = requestId;
    }
    toast.error(error.error.message || 'Failed to generate PDF');
    throw error;
  }

  const blob = await response.blob();
  const filename = extractFilename(response, fallbackFilename);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

/** Dashboard API client (business logic — products, tenants, payees, etc.) */
export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(API_BASE, path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) => request<T>(API_BASE, path, { method: 'POST', body }),

  /** Multipart POST for file uploads (e.g. a POS release APK). Goes through the
   *  shared request() so an upload gets the same auth refresh-and-retry as every
   *  other call — a raw fetch 403s outright when the access token is stale, and
   *  re-picking a 200 MB file to retry is not a good way to find that out. */
  postForm: <T>(path: string, formData: FormData) => request<T>(API_BASE, path, { method: 'POST', body: formData }),

  /** Multipart POST with upload progress — for large files such as APKs. */
  postFormWithProgress: <T>(path: string, formData: FormData, onProgress?: UploadProgress) =>
    uploadWithProgress<T>(API_BASE, path, formData, onProgress),

  /** POST that resolves to a Blob — for file downloads (e.g. packing-list PDFs). */
  postBlob: (path: string, body?: unknown) => requestBlob(API_BASE, path, body),

  /** PUT with an optional `tenantId` override that sets `X-Tenant-Id` — used
   *  when the super admin is operating cross-tenant from the admin pages.
   *  Without it the dashboard API resolves tenant from the JWT, which for a
   *  super admin is their own tenant and forbids writes against another. */
  put: <T>(path: string, body?: unknown, tenantId?: string) =>
    request<T>(API_BASE, path, {
      method: 'PUT',
      body,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  patch: <T>(path: string, body?: unknown) => request<T>(API_BASE, path, { method: 'PATCH', body }),

  delete: <T>(path: string) => request<T>(API_BASE, path, { method: 'DELETE' }),
};

/** Auth API client (authentication — login, signup, users, etc.) */
export const authApi = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(AUTH_BASE, path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) => request<T>(AUTH_BASE, path, { method: 'POST', body }),

  put: <T>(path: string, body?: unknown) => request<T>(AUTH_BASE, path, { method: 'PUT', body }),

  delete: <T>(path: string) => request<T>(AUTH_BASE, path, { method: 'DELETE' }),
};

/** Send Payments API client (vendor payments, payees, funding accounts) */
export const spApi = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(SP_BASE, path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) => request<T>(SP_BASE, path, { method: 'POST', body }),

  put: <T>(path: string, body?: unknown) => request<T>(SP_BASE, path, { method: 'PUT', body }),

  delete: <T>(path: string) => request<T>(SP_BASE, path, { method: 'DELETE' }),
};

/** Products API client (products, departments, tags, locations).
 *  Accepts an optional tenantId to set the X-Tenant-Id header — used by super
 *  admin when operating cross-tenant from the admin tenant detail page. */
export const productsApi = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>, tenantId?: string) =>
    request<T>(PRODUCTS_BASE, path, {
      method: 'GET',
      params,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  post: <T>(path: string, body?: unknown, tenantId?: string) =>
    request<T>(PRODUCTS_BASE, path, {
      method: 'POST',
      body,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  /** Multipart POST for file uploads (e.g. product/category/department images).
   *  Routes through the shared request() so uploads get the same 403 NO_TENANT
   *  refresh-and-retry as every other call — a raw fetch would 403 outright when
   *  the access token's tenantId is stale/empty. */
  postForm: <T>(path: string, formData: FormData, tenantId?: string) =>
    request<T>(PRODUCTS_BASE, path, {
      method: 'POST',
      body: formData,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  put: <T>(path: string, body?: unknown, tenantId?: string) =>
    request<T>(PRODUCTS_BASE, path, {
      method: 'PUT',
      body,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  delete: <T>(path: string, tenantId?: string) =>
    request<T>(PRODUCTS_BASE, path, {
      method: 'DELETE',
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),
};

/** Sync API client (Octopus POS sync, location mappings, sync status).
 *  Accepts an optional tenantId to set the X-Tenant-Id header (used by super admin). */
export const syncApi = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>, tenantId?: string) =>
    request<T>(SYNC_BASE, path, {
      method: 'GET',
      params,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  post: <T>(path: string, body?: unknown, tenantId?: string) =>
    request<T>(SYNC_BASE, path, {
      method: 'POST',
      body,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  put: <T>(path: string, body?: unknown, tenantId?: string) =>
    request<T>(SYNC_BASE, path, {
      method: 'PUT',
      body,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  delete: <T>(path: string, tenantId?: string) =>
    request<T>(SYNC_BASE, path, {
      method: 'DELETE',
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),
};

/** Receive Payments API client (invoices, customers, received payments) */
export const rpApi = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(RP_BASE, path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) => request<T>(RP_BASE, path, { method: 'POST', body }),

  put: <T>(path: string, body?: unknown) => request<T>(RP_BASE, path, { method: 'PUT', body }),

  delete: <T>(path: string) => request<T>(RP_BASE, path, { method: 'DELETE' }),

  downloadPdf: (
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
    fallbackFilename = 'statement.pdf',
  ) => downloadPdf(RP_BASE, path, params, fallbackFilename),
};

/** pos-backend API client — customer engagement endpoints.
 *  Accepts an optional tenantId to set the X-Tenant-Id header — used by super
 *  admin when operating cross-tenant (e.g. the customer migration import). */
export const posBackendApi = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>, tenantId?: string) =>
    request<T>(POS_BACKEND_BASE, path, {
      method: 'GET',
      params,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  post: <T>(path: string, body?: unknown, tenantId?: string) =>
    request<T>(POS_BACKEND_BASE, path, {
      method: 'POST',
      body,
      ...(tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : {}),
    }),

  put: <T>(path: string, body?: unknown) => request<T>(POS_BACKEND_BASE, path, { method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown) => request<T>(POS_BACKEND_BASE, path, { method: 'PATCH', body }),

  delete: <T>(path: string) => request<T>(POS_BACKEND_BASE, path, { method: 'DELETE' }),

  downloadPdf: (
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
    fallbackFilename = 'statement.pdf',
  ) => downloadPdf(POS_BACKEND_BASE, path, params, fallbackFilename),
};

// Electronic shelf labels. The browser talks only to us — the label platform's
// credentials live in the esl service and are never exposed to a client.
export const eslApi = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(ESL_BASE, path, { method: 'GET', params }),
  /** `headers` carries Idempotency-Key, which POST /items requires. */
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(ESL_BASE, path, { method: 'POST', body, headers }),
  put: <T>(path: string, body?: unknown) => request<T>(ESL_BASE, path, { method: 'PUT', body }),
  delete: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(ESL_BASE, path, { method: 'DELETE', params }),
};

/** ESL calls made by a SUPER ADMIN on behalf of another tenant. The ESL service
 *  honours X-Tenant-Id only for a super_admin token — for anyone else the header
 *  is ignored, so this cannot be used to reach across businesses. */
export const eslAdminApi = {
  get: <T>(path: string, tenantId: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(ESL_BASE, path, { method: 'GET', params, headers: { 'X-Tenant-Id': tenantId } }),
  post: <T>(path: string, tenantId: string, body?: unknown) =>
    request<T>(ESL_BASE, path, { method: 'POST', body, headers: { 'X-Tenant-Id': tenantId } }),
  put: <T>(path: string, tenantId: string, body?: unknown) =>
    request<T>(ESL_BASE, path, { method: 'PUT', body, headers: { 'X-Tenant-Id': tenantId } }),
  delete: <T>(path: string, tenantId: string) =>
    request<T>(ESL_BASE, path, { method: 'DELETE', headers: { 'X-Tenant-Id': tenantId } }),
};
